import React from "react";
import {Empty, Spin, Table, TableColumnsType, Typography} from "antd";
import dayjs from "dayjs";

import {getDistanceUnit} from "../../helpers/booking";
import {messageType, showMessages} from "../../helpers/message";
import {truncateString} from "../../helpers/misc";
import {dateTimeFormat} from "../../lib/time";
import {useLocation} from "react-router-dom";
import {useGetJourneyTripsQuery} from "../../store/api/bookingsApi";
import {BookingDrivingLogProps, JourneyTrip} from "../../types/bookingTypes";
import {BaseError} from "../../types/apiTypes";
import {useAppSelector} from "../../hooks/useStore";
import {selectHasStaffRole} from "../../store/slices/userSlice";

const BookingDrivingLog = ({bookingDetails}: BookingDrivingLogProps) => {
    const location = useLocation();
    const [page, setPage] = React.useState<number>(location?.state?.page ?? 1);
    const [perPage, setPerPage] = React.useState<number>(location?.state?.perPage ?? 10);
    const [total, setTotal] = React.useState<number>(10);

    const {data: journeyTrips, isFetching, isError, error, isSuccess} = useGetJourneyTripsQuery({journeyID: bookingDetails.journey_id, page, perPage});

    React.useEffect(() => {
        if (isSuccess && journeyTrips) {
            setTotal(journeyTrips?.meta?.total);
        } else if (isError && error) {
            showMessages((error as BaseError).data?.message, messageType.error);
        }
    }, [journeyTrips, error, isError, isSuccess]);

    const pageChange = (page: number, perPage: number) => {
        setPage(page);
        setPerPage(perPage);
    }

    const paginationAttr = {
        onChange: pageChange,
        showSizeChanger: true,
        onShowSizeChange: pageChange,
        total,
        perPage,
    }

    const emptyTable = {
        emptyText: <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={<span>No trips associated with this booking</span>}
        />
    };

    const columns: TableColumnsType<JourneyTrip> = [
        {
            title: <Typography.Text strong>Driver</Typography.Text>,
            dataIndex: 'person',
            key: 'person',
            render: (person, isMCT) => {
                return <span className="booking-driving-log-driver-line-item long-text-wrap">{isMCT ? person || 'N/A' : 'private'}</span>
            },
        },
        {
            title: <Typography.Text strong>Start Time</Typography.Text>,
            dataIndex: 'start_time',
            key: 'start_time',
            render: start_time => {
                return <span className="booking-driving-log-start-time-line-item long-text-wrap">{start_time ? dayjs(start_time).tz().format(dateTimeFormat) : 'N/A'}</span>
            },
        },
        {
            title: <Typography.Text strong>End Time</Typography.Text>,
            dataIndex: 'end_time',
            key: 'end_time',
            render: end_time => {
                return <span className="booking-driving-log-end-time-line-item long-text-wrap">{end_time ? dayjs(end_time).tz().format(dateTimeFormat) : 'N/A'}</span>
            },
        },
            {
            title: <Typography.Text strong>Distance ({getDistanceUnit()})</Typography.Text>,
            dataIndex: 'distance',
            key: 'distance',
            render: distance => {
                return <span className="booking-driving-log-distance-line-item long-text-wrap">{distance}</span>
            },
        },
            {
            title: <Typography.Text strong>End Location</Typography.Text>,
            dataIndex: 'end_location',
            key: 'end_location',
            render: end_location => {
                return <span className="booking-driving-end-location-line-item long-text-wrap">
                    {/* If both coordinates are null, or both are 0.000... then display N/A */}
                    {((end_location[0] && end_location[1]) && (end_location[0] % 1.1 !== Math.abs(0) || end_location[1] % 1.1 !== Math.abs(0))) ?
                        <a style={{'textDecoration': 'underline'}} target="_empty" href={`https://maps.google.com/?q=${end_location[0]},${end_location[1]}`}>
                            {truncateString(end_location[0], 6)} || {truncateString(end_location[1], 8)}
                        </a>
                        : 'N/A'
                    }
                </span>
            },
        },
    ];

    // Member's do not get to see the GeoLocation (EndLocation) of the trip.
    if (!useAppSelector(state => selectHasStaffRole(state, ['regular']))) {
        columns.pop();
    }

    return (
        <Spin spinning={isFetching}>
            <Table
                rowKey="id"
                dataSource={journeyTrips?.data}
                columns={columns}
                bordered={false}
                className="responsive-table"
                title={() => <Typography.Text>Driving Log</Typography.Text>}
                pagination={paginationAttr}
                locale={emptyTable}
            />
        </Spin>
    )
}

export default BookingDrivingLog;
